import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { RestfulService } from 'src-ironside/services/restful.service';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { Filter } from './common.repository';
import { DynamicViewInfoService } from 'src/app/shared/services/dynamic-view-info.service';
import { downloadBlob } from 'src-ironside/helpers/file';

const COLLECTION_NAME = 'dynamic_legal_entity';

export type Supplier = {
    id: string;
    title: string;
    phone: string;
    fax: string;
    email: string;
    city: string;
    code: string;
    logo: string;
    resources?: string[];
    companyName?: string;
};

export interface DynamicReferencedFilterParam {
    sorts: { [key: string]: string };
    page: number;
    pageSize: number;
    fields: string[];
    referencedDocumentName: string;
    referencedField: string;
    referencedFilters?: Array<{
        fieldName?: string;
        operator?: string;
        conditions?: Array<{
            type?: string;
            value?: string;
        }>;
    }>;
    referencedLocales?: string[];
    referencedTimezone?: number;
}

export interface DynamicContacParam {
    collectionName: string;
    filters: any;
    page: number;
    pageSize: number;
    sort: any;
}

@Injectable()
export class SupplierRepository extends RestfulService {
    constructor(
        private dynamicDocumentService: DynamicDocumentService,
        private dynamicViewService: DynamicViewInfoService,
        public override httpClient: HttpClient,
        public override router: Router
    ) {
        super(httpClient, router);
    }

    defaultFilters: Filter[] = [
        {
            fieldName: 'typeOfLegalEntity',
            conditions: [
                {
                    type: 'EQUALS',
                    value: 'FOURNISSEUR',
                },
            ],
        },
        {
            fieldName: 'supplierStatus',
            conditions: [
                {
                    type: 'EQUALS',
                    value: 'PUBLIE',
                },
            ],
        },
    ];

    find(
        page = 1,
        pageSize = 1000,
        direction: 'ASC' | 'DESC' = 'ASC',
        companyName: string = ''
    ): Observable<{ content: Supplier[]; totalElements: number }> {
        const filter: Filter[] = [...this.defaultFilters];

        if (companyName) {
            filter.push({
                fieldName: 'companyName',
                conditions: [
                    {
                        type: 'CONTAINS',
                        value: companyName,
                    },
                ],
            });
        }
        return this.doPost(`dynamic-documents/${COLLECTION_NAME}/search`, {
            collectionName: COLLECTION_NAME,
            filters: filter,
            page: page,
            pageSize: pageSize,
            sorts: {
                companyName: direction,
            },
        }).pipe(
            map((res) => {
                return {
                    content: res.content.map((supplier: any) => ({
                        id: supplier.id,
                        title: supplier.companyName,
                        phone: supplier.phone,
                        fax: supplier.fax,
                        email: supplier.email,
                        city: supplier.city,
                        code: supplier.code,
                        resources: supplier.linkToResources?.map(
                            (resource: any) => resource.documentId
                        ),
                        logo: supplier?.logo?.[0]?.url,
                    })),
                    totalElements: res.totalElements,
                };
            })
        );
    }
    getDetailSupplier(
        page = 1,
        pageSize = 150,
        direction: 'ASC' | 'DESC' = 'DESC',
        supplierId: string = ''
    ) {
        const filters: Filter[] = [
            {
                fieldName: 'code',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: supplierId,
                    },
                ],
            },
        ];
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: COLLECTION_NAME,
                    filters: filters,
                    page: page,
                    pageSize: pageSize,
                    sorts: {
                        publishedOrder: direction,
                    },
                },

                true
            )
            .pipe(
                map((res) => {
                    return res;
                })
            );
    }

    getDetailSupplierById(supplierId: string = '') {
        return this.dynamicDocumentService.findById(COLLECTION_NAME, supplierId, true).pipe(
            map((res) => {
                return res;
            })
        );
    }

    getViews() {
        return this.dynamicViewService.findByDocumentName(COLLECTION_NAME, false);
    }

    export(viewId: string) {
        this.dynamicDocumentService
            .exportFileSupplierOnView(COLLECTION_NAME, viewId, {
                ids: [],
                filters: [
                    ...this.defaultFilters.map((filter: Filter) => {
                        return {
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    valeurMin: '',
                                    value: filter.conditions[0].value,
                                    valeurMax: '',
                                },
                            ],
                            fieldName: filter.fieldName,
                        };
                    }),
                ],
                locales: ['fr_FR'],
                isUseLabel: 'true',
            })
            .subscribe((data: Blob) => {
                downloadBlob(data, 'xlsx');
            });
    }

    exportSupplierExtra(viewId: string, value: string) {
        const defaultFilters: Filter[] = [
            {
                fieldName: 'typeOfLegalEntity',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: value,
                    },
                ],
            },
        ];
        return this.dynamicDocumentService.exportFileSupplierOnViewExtra(COLLECTION_NAME, viewId, {
            ids: [],
            filters: [
                ...defaultFilters.map((filter: Filter) => {
                    return {
                        conditions: [
                            {
                                id: 0,
                                type: 'EQUALS',
                                valeurMin: '',
                                value: filter.conditions[0].value,
                                valeurMax: '',
                            },
                        ],
                        fieldName: filter.fieldName,
                    };
                }),
            ],
            locales: ['fr_FR'],
            isUseLabel: 'true',
        });
    }
}
